import React from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import axios, { AxiosError } from "axios";
import { apiClient } from "../services/api";

// Types pour l'entreprise (tenant)
export interface Tenant {
  id: string;
  name: string;
  status: "active" | "suspended" | "inactive";
}

// Types pour l'authentification
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: "super_admin" | "admin" | "driver";
  tenant_id?: string;
  phone?: string;
  avatar?: string;
  status: "active" | "inactive" | "suspended";
  permissions?: Record<string, boolean>;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  tenant?: Tenant | null;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  initialize: () => Promise<void>;
}

// Define API error response type
interface ApiErrorResponse {
  error?: string;
  message?: string;
}

// Configuration de l'API
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api";

// Store Zustand avec persistence fixée
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
      isInitialized: false,

      // Initialize authentication state and axios
      initialize: async () => {
        const state = get();

        if (state.isInitialized) {
          return;
        }

        if (!state.token) {
          set({ isInitialized: true });
          return;
        }

        set({ isLoading: true });

        try {
          // Set up axios with token and auth failure callback
          apiClient.setToken(state.token);
          apiClient.setOnAuthFailure(() => {
            get().logout();
          });

          // Make the API call
          const response = await apiClient.get("/auth/me");

          // 🔍 DEBUG LOG - Place it here!
          console.log("Auth response:", {
            response,
            data: response?.data,
            user: response?.data?.user,
          });

          // Validate response
          if (!response || !response.user) {
            throw new Error("Invalid response from server");
          }

          const userData = response.user;

          if (!userData || !userData.id) {
            throw new Error("Invalid user data received");
          }

          set({
            user: userData,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });
        } catch (error) {
          console.error("Failed to initialize auth:", error);

          apiClient.clearToken();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          });
        }
      },
      
      login: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          // Use axios directly for login to avoid circular dependency
          const response = await axios.post(`${API_URL}/auth/login`, {
            email,
            password,
          });

          if (!response.data) {
            throw new Error("Invalid response from server");
          }

          const { token, user } = response.data;

          if (!token || !user) {
            throw new Error("Invalid login response");
          }

          // Set token and auth failure callback
          apiClient.setToken(token);
          apiClient.setOnAuthFailure(() => {
            get().logout();
          });

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });
        } catch (error) {
          set({ isLoading: false });

          const axiosError = error as AxiosError<ApiErrorResponse>;
          const errorMessage =
            axiosError.response?.data?.error ||
            axiosError.response?.data?.message ||
            "Erreur de connexion";
          throw new Error(errorMessage);
        }
      },

      logout: () => {
        // Clear token from API client
        apiClient.clearToken();

        // Clear all auth state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
        });

        // Only redirect if not already on login page
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      },

      refreshUser: async () => {
        const { token } = get();

        if (!token) {
          throw new Error("No token available");
        }

        set({ isLoading: true });

        try {
          apiClient.setToken(token);
          const response = await apiClient.get("/auth/me");

          // 🔍 DEBUG LOG - Also here if needed
          console.log("Refresh user response:", {
            response,
            user: response?.user,
          });

          const userData = response.user;

          if (!userData || !userData.id) {
            throw new Error("Invalid user data received");
          }

          set({
            user: userData,
            isAuthenticated: true,
            isLoading: false,
          });

          return userData;
        } catch (error) {
          console.error("Error refreshing user:", error);
          set({ isLoading: false });
          throw error;
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, ...userData },
          });
        }
      },
    }),
    {
      name: "fleetbase-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
      }),
      onRehydrateStorage: () => {
        console.log("Starting rehydration...");
        return (state, error) => {
          if (error) {
            console.error("Failed to rehydrate auth store:", error);
            return;
          }

          if (state) {
            console.log("Rehydrated state with token:", !!state.token);
            if (state.token && !state.isInitialized) {
              setTimeout(() => {
                state.initialize().catch((err) => {
                  console.error(
                    "Failed to initialize auth after rehydration:",
                    err
                  );
                });
              }, 100);
            } else {
              state.isInitialized = true;
            }
          }
        };
      },
    }
  )
);

// Hook pour initialiser l'authentification au chargement
export const useAuthInitialization = () => {
  React.useEffect(() => {
    const state = useAuthStore.getState();

    // Only initialize if not already initialized
    if (!state.isInitialized) {
      state.initialize();
    }
  }, []);
};

// Export utility functions
export const hasRole = (user: User | null, role: string): boolean => {
  return user?.role === role;
};

export const hasPermission = (
  user: User | null,
  permission: string
): boolean => {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return user.permissions?.[permission] === true;
};

export const isSuperAdmin = (user: User | null): boolean => {
  return hasRole(user, "super_admin");
};

export const isAdmin = (user: User | null): boolean => {
  return hasRole(user, "admin") || isSuperAdmin(user);
};

export const isDriver = (user: User | null): boolean => {
  return hasRole(user, "driver");
};

export const isTenantActive = (user: User | null): boolean => {
  return user?.tenant?.status === "active";
};

export const getTenantName = (user: User | null): string => {
  return user?.tenant?.name || "Entreprise inconnue";
};

export const getTenantStatus = (user: User | null): string => {
  return user?.tenant?.status || "unknown";
};
